/**
 * Checklist operations — add, update, delete, reorder, promote, query.
 */

import Dexie from "dexie"
import { ObjectType, ObjectStatus, ChecklistSubType } from "./db-types.js"
import type { DbObject, TotodoDb } from "./db-types.js"
import { getDb, getNow, emitEvent } from "./runtime.js"
import {
  createTaskDefaults,
  distributeIndexes,
  nextIndex,
  objectStatusLabel,
} from "./utils.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"
import { getInsertIndex } from "./helpers/indexHelpers.js"
import { queueKey, runQueued, serializeTaskMoves } from "./helpers/queueHelpers.js"
import { uniqueDeletedAt } from "./helpers/trashHelpers.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { reopenContainerIfClosed } from "./containerStatusOps.js"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function checklistParentPath(
  taskParentPath: string,
  taskId: string,
): string {
  return `${taskParentPath}/${taskId}`
}

/**
 * Display order of the rows under one parent: finite `index` ascending, rows without a usable
 * index (missing, NaN, a string) after them, ties by id. Dexie's `sortBy("index")` compares a
 * missing index as NaN, which is not a consistent order, so one bad row could reshuffle the rest.
 *
 * The one checklist order: queryChecklistItems sorts by it, and the app's checklist sorts its
 * optimistic state by it too, so the app lists the rows in the order the API reports them.
 */
export function compareChecklistRows(
  a: Pick<DbObject, "id" | "index">,
  b: Pick<DbObject, "id" | "index">,
): number {
  const ai = Number.isFinite(a.index) ? a.index : Infinity
  const bi = Number.isFinite(b.index) ? b.index : Infinity
  if (ai !== bi) return ai < bi ? -1 : 1
  const aid = String(a.id)
  const bid = String(b.id)
  return aid < bid ? -1 : aid > bid ? 1 : 0
}

/**
 * The highest finite `index` among the live rows of `type` directly under `parentPath`, i.e. the
 * index of the last row in display order. Taking `.last()` on the `[parentPath+type]` index
 * instead returns the row with the highest *id* (a random UUID), deleted or not, so a row added
 * after it could reuse an index and appear in arbitrary order.
 */
async function highestLiveIndex(
  parentPath: string,
  type: ObjectType,
): Promise<number | undefined> {
  const rows = await getDb()
    .totodo.where("[parentPath+type]")
    .equals([parentPath, type])
    .toArray()
  let highest: number | undefined
  for (const row of rows) {
    if (row.isDeleted !== 0 || !Number.isFinite(row.index)) continue
    if (highest === undefined || row.index > highest) highest = row.index
  }
  return highest
}

/**
 * The checklist row `id`, deleted or not. A missing id, or one that is not a checklist row, is
 * "not_found" — REST answers 404, as it does for a wrong-type id.
 */
async function existingChecklistRow(id: string): Promise<OpResult<DbObject>> {
  const row = typeof id === "string" && id !== "" ? await getDb().totodo.get(id) : undefined
  if (!row) return fail(`Checklist item not found: ${id}`, "not_found")
  if (row.type !== ObjectType.Checklist) return fail(`${id} is not a checklist item`, "not_found")
  return ok(row)
}

/** The checklist row `id` if it exists, is a checklist row and is not deleted ("not_found" otherwise). */
async function liveChecklistRow(id: string): Promise<OpResult<DbObject>> {
  const row = await existingChecklistRow(id)
  if (!row.ok) return row
  if (row.data.isDeleted !== 0) return fail(`Checklist item is deleted: ${id}`, "not_found")
  return row
}

/**
 * Run `fn` — a read of the checklist followed by writes to it (append, insert, move) — in a rw
 * transaction, one call at a time per checklist, in call order.
 *
 * The transaction alone serialises such calls on IndexedDB, but the Durable Object's dexie-sqlite
 * does not isolate overlapping transactions: two parallel appends both read the same last index.
 * Hence the queue (queueHelpers). Inside a caller's transaction `fn` just joins it (waiting for
 * the queue there could let IndexedDB auto-commit the caller's transaction).
 */
function inChecklistTransaction<T>(
  parentPath: string,
  fn: () => Promise<T>,
): Promise<T> {
  const db = getDb()
  if (Dexie.currentTransaction) return db.transaction("rw", db.totodo, fn)
  return runQueued([queueKey("checklist", parentPath)], () =>
    db.transaction("rw", db.totodo, fn),
  )
}

/**
 * Index for a row appended at the end of the checklist under `parentPath` (read and written
 * inside inChecklistTransaction): last + 1, or — when that is no bigger than last (2^53 and up,
 * where + 1 rounds back) — the renumbering explicitInsertIndex falls back to.
 */
async function appendIndex(parentPath: string): Promise<number> {
  const last = await highestLiveIndex(parentPath, ObjectType.Checklist)
  if (last === undefined) return 0
  if (last + 1 > last) return last + 1
  return explicitInsertIndex(parentPath, last, undefined)
}

// ---------------------------------------------------------------------------
// Add
// ---------------------------------------------------------------------------

/**
 * The index for a row added with `afterIndex` / `beforeIndex`, read and written inside
 * inChecklistTransaction. Both given: between them, by the app's fractional rule (`nextIndex`);
 * when no representable index lies between them, the checklist is renumbered to make room, by
 * planChecklistInsert — the rule moveChecklistItem applies to a move. One given: afterIndex + 1 /
 * beforeIndex - 1, as before, renumbering the same way when that is not past the neighbour
 * (2^53 and up), which is also how appendIndex appends there.
 */
async function explicitInsertIndex(
  parentPath: string,
  afterIndex: number | undefined,
  beforeIndex: number | undefined,
): Promise<number> {
  const index = nextIndex(afterIndex, beforeIndex)
  const fits =
    Number.isFinite(index) &&
    (afterIndex == null || index > afterIndex) &&
    (beforeIndex == null || index < beforeIndex)
  if (fits) return index

  // The new row goes after the rows at or before afterIndex (before the rows at or after
  // beforeIndex when only that is given), wherever the renumbering puts them.
  const rows = await queryChecklistItems(parentPath)
  const usable = rows.filter((row) => Number.isFinite(row.index))
  const position = Number.isFinite(afterIndex)
    ? usable.filter((row) => row.index <= afterIndex!).length
    : Number.isFinite(beforeIndex)
      ? usable.filter((row) => row.index < beforeIndex!).length
      : rows.length
  const plan = planChecklistInsert(rows, position)
  if (plan.changes.length > 0) {
    const timestamp = getNow()
    await getDb().totodo.bulkUpdate(
      plan.changes.map((c) => ({ key: c.id, changes: { index: c.index, updatedAt: timestamp } })),
    )
  }
  return plan.indexes[0]!
}

/**
 * Add one checklist row. Without `afterIndex` / `beforeIndex` it goes after the last live row;
 * with them, see explicitInsertIndex. Reading the checklist and adding the row run as one
 * serialised step per checklist, so parallel adds get distinct indexes in call order.
 *
 * The row goes under the task's parentPath as it is when the add runs, not `taskParentPath`:
 * callers read that before the add is queued, and a move or schedule of the task in between
 * (serializeTaskMoves) cascades the checklist to the new path — a row added under the old one
 * would be reported created yet missing from every view of the task. So the add waits for any
 * such move, and holds the next one back, while it reads the task and writes the row. (Safe to
 * nest: no op holds a checklist queue and then waits for a task one.) `taskParentPath` is used
 * only when the task is not stored (yet).
 */
async function addChecklistRow(
  op: string,
  taskId: string,
  taskParentPath: string,
  title: string,
  subType: ChecklistSubType | undefined,
  afterIndex: number | undefined,
  beforeIndex: number | undefined,
  rowId: string | undefined,
): Promise<OpResult<{ id: string }>> {
  try {
    const id = rowId ?? crypto.randomUUID()

    await serializeTaskMoves([taskId], async () => {
      const task = await getDb().totodo.get(taskId)
      const parentPath = checklistParentPath(task?.parentPath ?? taskParentPath, taskId)

      await inChecklistTransaction(parentPath, async () => {
        const index =
          afterIndex == null && beforeIndex == null
            ? await appendIndex(parentPath)
            : await explicitInsertIndex(parentPath, afterIndex, beforeIndex)
        const timestamp = getNow()
        const realmId = await resolveContainerRealmId(parentPath)
        await getDb().totodo.add(
          createTaskDefaults({
            id,
            type: ObjectType.Checklist,
            ...(subType !== undefined ? { subType } : {}),
            title,
            parentPath,
            realmId,
            index,
            createdAt: timestamp,
            updatedAt: timestamp,
          }),
        )
      })
    })
    return ok({ id })
  } catch (e) {
    return fail(`${op} failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

export async function addChecklistItem(
  taskId: string,
  taskParentPath: string,
  title = "",
  afterIndex?: number,
  beforeIndex?: number,
  id?: string,
): Promise<OpResult<{ id: string }>> {
  return addChecklistRow(
    "addChecklistItem",
    taskId,
    taskParentPath,
    title,
    undefined,
    afterIndex,
    beforeIndex,
    id,
  )
}

export async function addChecklistHeading(
  taskId: string,
  taskParentPath: string,
  title: string,
  afterIndex?: number,
  beforeIndex?: number,
  id?: string,
): Promise<OpResult<{ id: string }>> {
  return addChecklistRow(
    "addChecklistHeading",
    taskId,
    taskParentPath,
    title,
    ChecklistSubType.Heading,
    afterIndex,
    beforeIndex,
    id,
  )
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

// Every single-row write below refuses an id that is missing, deleted or not a checklist row
// (Dexie's update() resolves 0 for a missing key, which used to be reported as success).

export async function updateChecklistTitle(
  id: string,
  title: string,
): Promise<OpResult> {
  const db = getDb()
  try {
    // An update with `title: undefined` would delete the field from the row.
    if (typeof title !== "string") return fail("updateChecklistTitle: title must be a string")
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    await db.totodo.update(id, {
      title,
      updatedAt: getNow(),
    })
    return ok()
  } catch (e) {
    return fail(
      `updateChecklistTitle failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateChecklistStatus(
  id: string,
  status: ObjectStatus,
): Promise<OpResult> {
  const db = getDb()
  try {
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    const timestamp = getNow()
    await db.totodo.update(id, {
      status,
      statusAt: timestamp,
      updatedAt: timestamp,
    })
    return ok()
  } catch (e) {
    return fail(
      `updateChecklistStatus failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function toggleChecklistItem(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const found = await liveChecklistRow(id)
    if (!found.ok) return found
    const item = found.data

    const newStatus =
      item.status === ObjectStatus.Completed
        ? ObjectStatus.Unfinished
        : ObjectStatus.Completed

    const result = await updateChecklistStatus(id, newStatus)
    if (result.ok) {
      emitEvent("checklist_toggled", { id, newStatus: objectStatusLabel(newStatus) })

      // Detect all-items-completed: if we just completed an item, check siblings
      if (newStatus === ObjectStatus.Completed) {
        try {
          const siblings = await db.totodo
            .where("[parentPath+type]")
            .equals([item.parentPath, ObjectType.Checklist])
            .filter((s) => s.isDeleted === 0 && s.subType !== ChecklistSubType.Heading)
            .toArray()
          const allCompleted = siblings.length > 0 && siblings.every((s) => s.status === ObjectStatus.Completed)
          if (allCompleted) {
            emitEvent("checklist_completed", { item_count: siblings.length })
          }
        } catch {
          // Non-critical — swallow
        }
      }
    }
    return result
  } catch (e) {
    return fail(
      `toggleChecklistItem failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateChecklistProgress(
  id: string,
  progress: number,
): Promise<OpResult> {
  const db = getDb()
  try {
    if (!Number.isFinite(progress)) return fail("updateChecklistProgress: progress must be a finite number")
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    await db.totodo.update(id, {
      progress: Math.max(0, Math.min(100, Math.round(progress))),
      updatedAt: getNow(),
    })
    return ok()
  } catch (e) {
    return fail(
      `updateChecklistProgress failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateChecklistSubType(
  id: string,
  subType: number,
): Promise<OpResult> {
  const db = getDb()
  try {
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    await db.totodo.update(id, {
      subType,
      ...(subType === ChecklistSubType.Heading
        ? {
            status: ObjectStatus.Unfinished,
            progress: 0,
            userStatus: undefined,
            depth: 0,
          }
        : {}),
      updatedAt: getNow(),
    })
    return ok()
  } catch (e) {
    return fail(
      `updateChecklistSubType failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

/** The id of the task whose checklist the row is in: the last segment of its parentPath. */
function checklistTaskId(row: DbObject): string {
  const path = typeof row.parentPath === "string" ? row.parentPath : ""
  return path.slice(path.lastIndexOf("/") + 1)
}

/**
 * The objects above a checklist row that are in the trash — its task, the task's containers.
 * Their deletedAt values are the stamps restoreCascade matches the row against.
 */
async function trashedOwners(row: DbObject): Promise<DbObject[]> {
  if (typeof row.parentPath !== "string") return []
  const ids = row.parentPath.split("/").filter((s) => s !== "" && s !== "root" && !s.startsWith("#"))
  const owners = await getDb().totodo.bulkGet(ids)
  return owners.filter((o): o is DbObject => o !== undefined && o.isDeleted === 1)
}

/**
 * Soft-delete a checklist row. Idempotent: deleting a row that is already deleted succeeds with
 * `alreadyDeleted: true`, so a retried delete succeeds. An id that never was a checklist row is
 * "not_found".
 *
 * The row gets a deletedAt of its own, never the stamp of its trashed task (or of a trashed
 * container above it): restoreCascade would take the row for part of that object's cascade and
 * bring it back with it. That holds for a row that is already deleted too: one that went to the
 * trash with its task carries the task's stamp, and deleting it now gives it its own, so
 * restoring the task leaves it deleted. A row deleted on its own before keeps its deletedAt.
 *
 * Runs under the task's queue (serializeTaskMoves), as deleteTask and restoreFromTrash do, so a
 * task delete cannot read the row live and then overwrite this stamp with its own.
 */
export async function deleteChecklistItem(
  id: string,
): Promise<OpResult<{ alreadyDeleted: boolean }>> {
  const db = getDb()
  try {
    const found = await existingChecklistRow(id)
    if (!found.ok) return found
    // A move of a container above the task changes the row's path, never its last segment
    return await serializeTaskMoves([checklistTaskId(found.data)], async () => {
      const current = await existingChecklistRow(id)
      if (!current.ok) return current
      const row = current.data
      const owners = await trashedOwners(row)
      const timestamp = getNow()
      if (row.isDeleted !== 0) {
        const withOwner = owners.some((o) => o.deletedAt !== undefined && o.deletedAt === row.deletedAt)
        if (withOwner) {
          await db.totodo.update(id, {
            deletedAt: uniqueDeletedAt(timestamp, owners),
            updatedAt: timestamp,
          })
        }
        return ok({ alreadyDeleted: true })
      }
      await db.totodo.update(id, {
        isDeleted: 1,
        deletedAt: uniqueDeletedAt(timestamp, owners),
        updatedAt: timestamp,
      })
      return ok({ alreadyDeleted: false })
    })
  } catch (e) {
    return fail(
      `deleteChecklistItem failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Reorder
// ---------------------------------------------------------------------------

/**
 * Set a row's raw sort key. The app computes `newIndex` itself (fractional, between the new
 * neighbours — see `reorderIndex`); to move a row to a position, use `moveChecklistItem`.
 */
export async function reorderChecklistItem(
  id: string,
  newIndex: number,
): Promise<OpResult> {
  const db = getDb()
  try {
    if (!Number.isFinite(newIndex)) return fail("reorderChecklistItem: index must be a finite number")
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    await db.totodo.update(id, {
      index: newIndex,
      updatedAt: getNow(),
    })
    return ok()
  } catch (e) {
    return fail(
      `reorderChecklistItem failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/** Are the rows' indexes finite and strictly increasing (no ties)? */
function strictlyIncreasing(rows: readonly Pick<DbObject, "index">[]): boolean {
  for (let i = 0; i < rows.length; i++) {
    if (!Number.isFinite(rows[i]!.index)) return false
    if (i > 0 && !(rows[i]!.index > rows[i - 1]!.index)) return false
  }
  return true
}

/** A row's new sort key, as `planChecklistMove` gives it. */
export interface ChecklistIndexChange {
  id: string
  index: number
}

/**
 * The index changes that move the rows `movingIds` (kept in display order, as a block) to
 * `position`: their 0-based place among the OTHER rows. `rows` is the checklist in display order.
 * Pure — the app's drag and ⌘↑ / ⌘↓ apply the changes optimistically and persist them with
 * reorderChecklistItem; moveChecklistItem (MCP, REST) writes them itself.
 *
 * Normally only the moved rows change: they get indexes strictly between their new neighbours,
 * by the app's fractional rule (`distributeIndexes`; `nextIndex` for one row). When no such
 * indexes exist — neighbours too close, or tied / unusable indexes in the list — every row is
 * renumbered 0..n-1 in the new order. Either way the indexes are unique and in display order
 * afterwards. A move to where the rows already are changes nothing.
 */
export function planChecklistMove(
  rows: readonly Pick<DbObject, "id" | "index">[],
  movingIds: readonly string[],
  position: number,
): ChecklistIndexChange[] {
  const moving = new Set(movingIds)
  const moved = rows.filter((row) => moving.has(String(row.id)))
  const others = rows.filter((row) => !moving.has(String(row.id)))
  if (moved.length === 0) return []
  const to = Number.isFinite(position)
    ? Math.max(0, Math.min(others.length, Math.trunc(position)))
    : others.length
  const ordered = [...others.slice(0, to), ...moved, ...others.slice(to)]

  if (strictlyIncreasing(rows) && ordered.every((row, i) => row === rows[i])) return []

  if (strictlyIncreasing(others)) {
    const prev = others[to - 1]?.index
    const next = others[to]?.index
    const indexes = distributeIndexes(prev, next, moved.length)
    const fits = indexes.every(
      (index, i) =>
        Number.isFinite(index) &&
        (i === 0 ? prev === undefined || index > prev : index > indexes[i - 1]!) &&
        (next === undefined || index < next),
    )
    if (fits) return moved.map((row, i) => ({ id: String(row.id), index: indexes[i]! }))
  }

  // No representable gap: renumber the whole checklist in the requested order.
  return ordered.flatMap((row, i) =>
    row.index === i ? [] : [{ id: String(row.id), index: i }],
  )
}

/** Where `count` new rows go, as `planChecklistInsert` gives it. */
export interface ChecklistInsertPlan {
  /** The new rows' indexes, in order. */
  indexes: number[]
  /** Index changes of existing rows that make room (usually none). */
  changes: ChecklistIndexChange[]
}

/**
 * Indexes for `count` new rows inserted at `position` — their 0-based place among `rows`, the
 * checklist in display order — and the changes to existing rows that make room for them. Pure:
 * the app's Enter, split, ⌘D and paste apply it optimistically; addChecklistItem with
 * afterIndex / beforeIndex writes it.
 *
 * Normally the new rows get the app's fractional indexes between their new neighbours
 * (`distributeIndexes`) and nothing else changes. When no such indexes exist — neighbours too
 * close for the float precision, or a row above without a usable index — the rows are placed by
 * planChecklistMove's rule, the one moveChecklistItem and the drag use: the checklist is
 * renumbered 0..n in the new order. Either way they land exactly at `position`.
 */
export function planChecklistInsert(
  rows: readonly Pick<DbObject, "id" | "index">[],
  position: number,
  count = 1,
): ChecklistInsertPlan {
  if (count <= 0) return { indexes: [], changes: [] }
  const to = Number.isFinite(position)
    ? Math.max(0, Math.min(rows.length, Math.trunc(position)))
    : rows.length
  const above = rows[to - 1]
  // Rows without a usable index sort last: a new row with a finite index goes before them anyway
  const below = rows[to]
  const prev = above?.index
  const next = below && Number.isFinite(below.index) ? below.index : undefined

  if (!above || Number.isFinite(prev)) {
    const indexes = distributeIndexes(prev, next, count)
    const fits = indexes.every(
      (index, i) =>
        Number.isFinite(index) &&
        (i === 0 ? prev === undefined || index > prev : index > indexes[i - 1]!) &&
        (next === undefined || index < next),
    )
    if (fits) return { indexes, changes: [] }
  }

  // No room: plan it as a move of the new rows (no index yet) to `position`.
  const newIds = Array.from({ length: count }, (_, i) => `\u0000new-${i}`)
  const changes = planChecklistMove(
    [...rows, ...newIds.map((id) => ({ id, index: Number.NaN }))],
    newIds,
    to,
  )
  const placed = new Map(changes.map((c) => [c.id, c.index]))
  return {
    indexes: newIds.map((id) => placed.get(id)!),
    changes: changes.filter((c) => !newIds.includes(c.id)),
  }
}

/**
 * Move a checklist row to `position`: its 0-based place among the checklist's live rows (items
 * and headings) in display order, clamped to the list — by `planChecklistMove`, the rule the
 * app's drag uses too. Every live index is unique afterwards. Runs serialised per checklist,
 * like the appends.
 */
export async function moveChecklistItem(
  id: string,
  position: number,
): Promise<OpResult<{ position: number; index: number }>> {
  const db = getDb()
  try {
    if (!Number.isFinite(position)) return fail("moveChecklistItem: position must be a finite number")
    const found = await liveChecklistRow(id)
    if (!found.ok) return found
    const parentPath = found.data.parentPath

    return await inChecklistTransaction(parentPath, async () => {
      const rows = await queryChecklistItems(parentPath)
      const moving = rows.find((row) => row.id === id)
      if (!moving) return fail(`Checklist item not found: ${id}`)
      const to = Math.max(0, Math.min(rows.length - 1, Math.trunc(position)))

      const changes = planChecklistMove(rows, [id], to)
      if (changes.length > 0) {
        const timestamp = getNow()
        await db.totodo.bulkUpdate(
          changes.map((c) => ({ key: c.id, changes: { index: c.index, updatedAt: timestamp } })),
        )
      }
      return ok({ position: to, index: changes.find((c) => c.id === id)?.index ?? moving.index })
    })
  } catch (e) {
    return fail(
      `moveChecklistItem failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Promote
// ---------------------------------------------------------------------------

export async function promoteChecklistToTask(
  id: string,
  newParentPath: string,
): Promise<OpResult> {
  const db = getDb()
  try {
    const row = await liveChecklistRow(id)
    if (!row.ok) return row
    const timestamp = getNow()

    // At the bottom, as moveTask and addTask place a task: after the last live sibling of any
    // type — a trailing section shares the index space, and the task must not tie with it.
    const index = await getInsertIndex(newParentPath, "bottom", ObjectType.Task)

    const realmId = await resolveContainerRealmId(newParentPath)

    // An open task promoted into a closed project / list reopens it (first)
    if (row.data.status === ObjectStatus.Unfinished) await reopenContainerIfClosed(newParentPath)

    await db.totodo.update(id, {
      type: ObjectType.Task,
      subType: undefined,
      parentPath: newParentPath,
      realmId,
      index,
      updatedAt: timestamp,
    })
    return ok()
  } catch (e) {
    return fail(
      `promoteChecklistToTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Query — the checklist rows of a task (shared by the app and the server)
// ---------------------------------------------------------------------------

/**
 * The live checklist rows (items and headings) under `parentPath` — the
 * checklist path of a task, see `checklistParentPath` — in display order.
 *
 * Single source of truth: the task-detail checklist (client live query) and
 * the server's `getTaskDetail` / MCP / REST views read the rows through this.
 *
 * `db` defaults to the ops context's instance. The client passes its live `db`
 * binding instead: the guest share page swaps that binding to guestDb, and the
 * ops context does not follow the swap.
 */
export function queryChecklistItems(
  parentPath: string,
  db: Pick<TotodoDb, "totodo"> = getDb(),
): Promise<DbObject[]> {
  // Filtered after toArray(): a Dexie .filter() walks a cursor row by row, toArray() reads in bulk.
  return db
    .totodo.where("[parentPath+type]")
    .equals([parentPath, ObjectType.Checklist])
    .toArray()
    .then((rows) => rows.filter((item) => item.isDeleted === 0).sort(compareChecklistRows))
}

export interface ChecklistProgress {
  completed: number
  total: number
}

/**
 * The checklist progress rule — the app's task header and the API views both use it: items only
 * (headings are not counted), only Completed counts as done, Canceled stays in `total`.
 * `rows` are live checklist rows, as `queryChecklistItems` returns them.
 */
export function checklistProgress(rows: readonly DbObject[]): ChecklistProgress {
  let completed = 0
  let total = 0
  for (const row of rows) {
    if (row.subType === ChecklistSubType.Heading) continue
    total++
    if (row.status === ObjectStatus.Completed) completed++
  }
  return { completed, total }
}

/** One checklist row as the API shows it (MCP `get_task_detail`, REST). */
export interface ChecklistRowView {
  id: string
  title: string
  kind: "item" | "heading"
  status: "open" | "completed" | "canceled"
  /** 0-100 for items; null for headings (they have no progress). */
  progress: number | null
  index: number
  /** Indent level (0 = top level); a row is nested under the closest row above with a lower depth. */
  depth: number
}

/** Rows one checklist view returns unless asked for fewer (get_task_detail, REST). */
export const CHECKLIST_VIEW_DEFAULT_LIMIT = 200
/** The most rows one checklist view returns. */
export const CHECKLIST_VIEW_MAX_LIMIT = 1000

/** Which slice of the checklist (in display order) a view returns. */
export interface ChecklistViewPage {
  /** Rows to skip (default 0). */
  offset?: number
  /** Rows to return (default 200, clamped to 1..1000). */
  limit?: number
}

export interface ChecklistView {
  /** Items and headings in display order — the requested page of them. */
  checklist: ChecklistRowView[]
  /** Over the WHOLE checklist, not just this page (see `checklistProgress`). */
  checklistProgress: ChecklistProgress
  /** Live rows (items and headings) in the whole checklist. */
  checklistTotal: number
  /** More rows follow this page: ask again with offset = offset + checklist.length. */
  checklistTruncated: boolean
}

function clampInt(value: number | undefined, fallback: number, min: number, max: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback
  return Math.max(min, Math.min(max, Math.trunc(value)))
}

/**
 * One row in the API shape. Storage does not validate rows (old clients, sync, a bad write), so
 * every field is coerced to what the schemas promise instead of failing the whole read. A row
 * without a usable index reports the one after `previousIndex` — it is listed after it.
 */
function toChecklistRowView(row: DbObject, previousIndex: number | undefined): ChecklistRowView {
  const kind = row.subType === ChecklistSubType.Heading ? "heading" : "item"
  const status =
    row.status === ObjectStatus.Completed ? "completed"
    : row.status === ObjectStatus.Canceled ? "canceled"
    : "open"
  const progress = Number.isFinite(row.progress) ? Math.max(0, Math.min(100, row.progress!)) : 0
  const depth = Number.isFinite(row.depth) && row.depth! > 0 ? Math.floor(row.depth!) : 0
  return {
    id: String(row.id),
    title: typeof row.title === "string" ? row.title : "",
    kind,
    status,
    progress: kind === "item" ? progress : null,
    index: Number.isFinite(row.index) ? row.index : (previousIndex ?? -1) + 1,
    depth,
  }
}

/**
 * Map raw rows from `queryChecklistItems` to the API view (pure): the requested page of rows,
 * progress and total over all of them.
 */
export function toChecklistView(
  rows: readonly DbObject[],
  page: ChecklistViewPage = {},
): ChecklistView {
  const offset = clampInt(page.offset, 0, 0, Number.MAX_SAFE_INTEGER)
  // At least one row: an empty page marked truncated would stall "offset + checklist.length" paging
  const limit = clampInt(page.limit, CHECKLIST_VIEW_DEFAULT_LIMIT, 1, CHECKLIST_VIEW_MAX_LIMIT)
  const end = Math.min(rows.length, offset + limit)

  const checklist: ChecklistRowView[] = []
  let previousIndex: number | undefined
  for (let i = 0; i < end; i++) {
    const view = toChecklistRowView(rows[i]!, previousIndex)
    previousIndex = view.index
    if (i >= offset) checklist.push(view)
  }
  return {
    checklist,
    checklistProgress: checklistProgress(rows),
    checklistTotal: rows.length,
    checklistTruncated: end < rows.length,
  }
}

/** The checklist view of the task `taskId` living under `taskParentPath`. */
export async function getChecklistView(
  taskId: string,
  taskParentPath: string,
  page?: ChecklistViewPage,
): Promise<ChecklistView> {
  return toChecklistView(
    await queryChecklistItems(checklistParentPath(taskParentPath, taskId)),
    page,
  )
}

// ---------------------------------------------------------------------------
// Query — get checklist items for a task
// ---------------------------------------------------------------------------

export interface ChecklistItemInfo {
  id: string
  title: string
  completed: boolean
  canceled: boolean
  index: number
}

/**
 * Get all checklist items for a task.
 * Resolves the checklist parentPath internally — caller only needs taskId.
 */
export async function getChecklistItems(
  taskId: string,
): Promise<OpResult<ChecklistItemInfo[]>> {
  const db = getDb()
  try {
    const task = await db.totodo.get(taskId)
    if (!task) return fail(`Task not found: ${taskId}`)

    const clPath = checklistParentPath(task.parentPath, taskId)
    const items = await db.totodo
      .where("parentPath")
      .equals(clPath)
      .filter((item) => item.isDeleted !== 1)
      .sortBy("index")

    return ok(
      items.map((item) => ({
        id: item.id as string,
        title: item.title,
        completed: item.status === ObjectStatus.Completed,
        canceled: item.status === ObjectStatus.Canceled,
        index: item.index,
      })),
    )
  } catch (e) {
    return fail(
      `getChecklistItems failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}
